import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from "@angular/common/http";
import { BehaviorSubject, EMPTY, Observable, of, throwError } from "rxjs";
import { UiErrorMessage } from "@assoanalytics/app/core/error/ui-error-message";
import { switchMap, filter, take, catchError, finalize, tap } from "rxjs/operators";
import { AuthService } from "@assoanalytics/app/core/services/auth.service";
import { NGXLogger } from "ngx-logger";
import { StorageService } from "@assoanalytics/app/core/services/storage.service";
import { environment } from "@assoanalytics/environments/environment";
import { Router } from "@angular/router";
import { defaults } from "@assoanalytics/app/app.config";
import { SnackbarService } from "@assoanalytics/app/shared/services/snackbar/snackbar.service";
import { ErrorFormatter } from "@assoanalytics/app/core/utils/error-formatter";
import { TokenService } from "../services/token.service";
import { GlobalErrorHandler } from "../error/global-error-handler";
import { EventService } from "../services/event.service";
import { RequestCacheService } from "@assoanalytics/app/core/services/request-cache.service";

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private tokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private storageService: StorageService,
    private tokenService: TokenService,
    private logger: NGXLogger,
    private router: Router,
    private snackbarService: SnackbarService,
    private cacheService: RequestCacheService,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Esclusione path
    if (this.shouldExclude(request.url)) {
      this.logger.debug(
        request.url,
        "Interceptor. Excluding path from interceptor",
      );

      // Controllo per login/token/refresh
      if (this.isSecureAuthEndpoint(request.url)) {
        let headers: any = {
          "X-Auth-Mode": "secure",
        };

        return next.handle(
          request.clone({
            setHeaders: headers,
          }),
        );
      }

      return next
        .handle(request)
        .pipe(catchError((err) => this.handleRequestError(err, request)));
    }

    // Controllo cache: se la richiesta è cacheabile e i dati sono ancora freschi,
    // restituisce la risposta salvata in-memory senza contattare il backend.
    if (this.cacheService.isCacheable(request)) {
      const cachedResponse = this.cacheService.get(request);
      if (cachedResponse) {
        this.logger.debug(`[Cache] HIT: ${request.urlWithParams}`);
        return of(cachedResponse as HttpEvent<unknown>);
      }
    }

    // Se procedura di refreshing avviata sottoscrivi solo al token emitter
    if (this.isRefreshing) {
      return this.tokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          return this.handleWithCache(
            request,
            this.addTokenHeader(request, token),
            next,
          );
        }),
      );
    }

    const token = this.tokenService.getToken();

    if (!token) {
      this.logger.debug(
        "Missing token, failing request",
        request.urlWithParams,
      );
      return this.interceptorError("Missing token");
    }

    // Se token non valido
    else if (!this.tokenService.validTokenFormat(token)) {
      this.logger.error("Invalid token format");
      return this.interceptorError("Invalid token format");
    }

    // Se il token è scaduto, accoda la richiesta e rinnova il token
    else if (this.authService.isTokenExpired(token)) {
      this.logger.debug("Token expired!");
      return this.handleTokenRefresh(request, next);
    }

    // Se il token è vicino alla scadenza, accoda la richiesta e rinnova il token
    else if (this.authService.isTokenNearToExpire(token)) {
      this.logger.debug("Token near to expire!");
      return this.handleTokenRefresh(request, next, token);
    } else {
      return this.handleWithCache(
        request,
        this.addTokenHeader(request, token),
        next,
      );
    }
  }

  private handleTokenRefresh(
    req: HttpRequest<any>,
    next: HttpHandler,
    token?: string,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.tokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((newToken) => {
          this.tokenSubject.next(newToken.access_token);

          // continua la prima richiesta che ha generato il refresh
          return this.handleWithCache(
            req,
            this.addTokenHeader(req, newToken.access_token),
            next,
          );
        }),
        catchError(() => {
          return this.interceptorError("OAuth Error");
        }),
        finalize(() => {
          this.isRefreshing = false;
        }),
      );
    } else {
      // Sottoscrivi la richiesta al token emitter finché non è rinnovato
      return this.tokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          return this.handleWithCache(
            req,
            this.addTokenHeader(req, token),
            next,
          );
        }),
      );
    }
  }

  /**
   * Esegue la chiamata HTTP con l'header di autenticazione e,
   * in caso di risposta positiva, salva il risultato in cache
   * usando la richiesta originale (priva di header auth) come chiave.
   *
   * @param originalRequest - richiesta originale (usata come chiave di cache)
   * @param authenticatedRequest - richiesta con Bearer token aggiunto
   * @param next - handler HTTP Angular
   */
  private handleWithCache(
    originalRequest: HttpRequest<any>,
    authenticatedRequest: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(authenticatedRequest).pipe(
      tap((event) => {
        if (
          this.cacheService.isCacheable(originalRequest) &&
          event instanceof HttpResponse &&
          event.ok
        ) {
          this.cacheService.put(originalRequest, event);
          this.logger.debug(`[Cache] STORED: ${originalRequest.urlWithParams}`);
        }
      }),
      catchError((err) => this.handleRequestError(err, originalRequest)),
    );
  }

  private handleRequestError(
    err: HttpErrorResponse,
    req: HttpRequest<any>,
  ): Observable<never> {
    // Gestione skip-error-handler per traduzioni
    if (req.headers.has("skip-error-handler")) {
      return EMPTY;
    }

    const errorMessage = err.message || err.error?.error || "Unknown error";

    GlobalErrorHandler.lastErrorMessage =
      err.error?.message || err.error?.error || err.message || err.error || "";

    // Emetto l'errore nell'EventService così da far decidere ad altri componenti se dare ulteriori dettagli o meno
    EventService.lastError.emit(err);

    switch (err.status) {
      case 401:
        const token = this.tokenService.getToken();

        if (token && this.authService.isTokenExpired(token)) {
          // Se il token è scaduto, esegue il logout
          return this.interceptorError(
            errorMessage,
            "Session expired. Please login again.",
          );
        } else {
          // Se è un errore generico 401, mostra solo un avviso
          return this.interceptorError(
            errorMessage,
            "Invalid credentials or unauthorized.",
            false,
          );
        }
      case 403:
        return this.interceptorError(
          errorMessage,
          "Session expired. Please login again.",
        );
      case 400:
        // ricevuto in attivazione mfa - invalid otp code
        return throwError(() => new Error("Invalid request."));

      case 500:
      case 502:
      case 503:
      case 504:
        // Formatta l'errore del server in modo più leggibile
        const formattedServerError = ErrorFormatter.formatHttpError(err);
        console.error("Server error", formattedServerError);
        this.snackbarService.error(
          "Server error",
          "CLOSE",
          formattedServerError,
          8000,
          true,
        );

        return EMPTY; // Utilizziamo EMPTY invece di of() per non emettere valori

      case 404:
        // Gestione locale per 404 - non interrompe l'applicazione
        const notFoundMessage = "Data not found";
        console.warn(notFoundMessage, err.url);
        this.snackbarService.warning(notFoundMessage, "Ok", 5000, true);
        return EMPTY;

      default:
        try {
          // Per errori di rete specifici, usa messaggi più descrittivi
          if (err.status === 0 && err.statusText === "Unknown Error") {
            return throwError(() => new Error("Network error"));
          }

          // Solo per il caso default usiamo UiErrorMessage per mostrare dettagli aggiuntivi
          const formattedErrorMessage = ErrorFormatter.formatHttpError(err);

          // Per errori di rete o sconosciuti, mostriamo la snackbar ma non interrompiamo l'app
          if (err.status === 0 || err.name === "HttpErrorResponse") {
            this.snackbarService.error(
              "Connection problem",
              "CLOSE",
              formattedErrorMessage,
            );
            return EMPTY;
          }

          return throwError(
            () => new UiErrorMessage(formattedErrorMessage, err),
          );
        } catch (e) {
          console.error("Error in default error handler:", e);
          // Fallback in caso di problemi
          return throwError(() => new Error("Network or unknown error."));
        }
    }
  }

  private addTokenHeader(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    let headers: any = {};

    headers = {
      Authorization: `Bearer ${token}`,
    };

    return request.clone({
      setHeaders: headers,
    });
  }

  /**
   * @description Path da escludere
   * @param url
   * @private
   */
  private shouldExclude(url: string): boolean {
    const excludedUrls = [
      this.buildUrl(environment.oauth, "login"),
      this.buildUrl(environment.oauth, "refresh"),
      this.buildUrl(environment.oauth, "token"),
      this.buildUrl(environment.oauth, "revoke"),
      this.buildUrl(environment.oauth, "reset-password"),
      this.buildUrl(environment.oauth, "mfa/verify"),
      this.buildUrl(environment.api, "signup"),
    ];

    return url.startsWith("./") || excludedUrls.some((u) => url.startsWith(u));
  }

  /**
   * @description Verifica se l'endpoint è login, token o refresh
   * @param url
   * @private
   */
  private isSecureAuthEndpoint(url: string): boolean {
    const secureAuthEndpoints = [
      this.buildUrl(environment.oauth, "login"),
      this.buildUrl(environment.oauth, "mfa/verify"),
      this.buildUrl(environment.oauth, "token"),
      this.buildUrl(environment.oauth, "refresh"),
      this.buildUrl(environment.oauth, "revoke"),
    ];

    return secureAuthEndpoints.some((endpoint) => url.startsWith(endpoint));
  }

  /**
   * @description Funzione di utilità per ottenere solo il dominio corrente senza slash finale
   * @returns
   */
  getCurrentDomain = () => {
    // window.location.origin include protocollo + dominio + eventuale porta
    // Rimuove eventuale slash finale
    return window.location.origin.replace(/\/$/, "");
  };

  private buildUrl(base: string, endpoint: string): string {
    const resolvedBase = this.resolveBaseUrl(base);
    const normalizedEndpoint = endpoint.replace(/^\/+/, "");
    return `${resolvedBase}${normalizedEndpoint}`;
  }

  private resolveBaseUrl(base: string): string {
    const trimmedBase = (base || "").trim();

    if (!trimmedBase) {
      return environment.production ? `${this.getCurrentDomain()}/` : "/";
    }

    if (/^https?:\/\//i.test(trimmedBase)) {
      return `${trimmedBase.replace(/\/+$/, "")}/`;
    }

    const normalizedRelativeBase = trimmedBase
      .replace(/^\/+/, "")
      .replace(/\/+$/, "");

    if (environment.production) {
      return `${this.getCurrentDomain()}/${normalizedRelativeBase}/`;
    }

    return `/${normalizedRelativeBase}/`;
  }

  private interceptorError(
    msg: string,
    uiMsg: string = "",
    forceLogout: boolean = true,
  ) {
    if (forceLogout) {
      console.error(msg);
      this.authService.logout();
    }

    if (uiMsg) {
      this.snackbarService.warning(uiMsg, "Ok", 5000, true);
    }
    return EMPTY;
  }
}
